// 監査ログ閲覧ページ（§11-2）。経営側（事務局・総経理）のみ。読み取り専用。
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { SelectInput, Spinner } from "../../components/ui";
import { AUDIT_ACTION_LABELS, AUDIT_ACTION_OPTIONS } from "../../masters";
import { formatDateTime } from "../../lib/format";
import { api, ApiError } from "../../lib/api";
import { useAuth } from "../../auth";
import { t } from "../../i18n/zh-TW";
import type { AuditEntry } from "../../lib/types";

export function AuditLogPage() {
  const { user } = useAuth();
  const isManagement = user?.role === "secretariat" || user?.role === "gm";

  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState("");

  useEffect(() => {
    if (!isManagement) return;
    setLoading(true);
    setError(null);
    api
      .listAudit({ action: action || undefined, limit: 300 })
      .then((res) => setEntries(res.entries))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : t.errors.loadFailed))
      .finally(() => setLoading(false));
  }, [isManagement, action]);

  // 経営側以外はアクセス不可。
  if (user && !isManagement) return <Navigate to="/" replace />;

  return (
    <div>
      <div className="mb-1 flex items-baseline gap-2">
        <h1 className="text-xl font-semibold text-slate-800">{t.audit.title}</h1>
        <span className="text-xs text-slate-400">{t.audit.subtitle}</span>
      </div>

      <div className="mb-4 mt-3">
        <label className="text-sm">
          <span className="mb-1 block text-xs text-slate-500">{t.audit.filterAction}</span>
          <SelectInput
            options={AUDIT_ACTION_OPTIONS}
            value={action}
            onChange={(e) => setAction(e.target.value)}
          />
        </label>
      </div>

      {loading ? (
        <div className="py-16">
          <Spinner label={t.common.loading} />
        </div>
      ) : error ? (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : entries.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-400">
          {t.audit.empty}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">{t.audit.columnTime}</th>
                <th className="px-4 py-3">{t.audit.columnActor}</th>
                <th className="px-4 py-3">{t.audit.columnAction}</th>
                <th className="px-4 py-3">{t.audit.columnTarget}</th>
                <th className="px-4 py-3">{t.audit.columnDetail}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.map((e) => (
                <tr key={e.id}>
                  <td className="whitespace-nowrap px-4 py-2.5 text-sm text-slate-600">
                    {formatDateTime(e.created_at)}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-700">{e.actor_email}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-800">
                    {AUDIT_ACTION_LABELS[e.action] ?? e.action}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">
                    {e.target_id ? `${t.audit.contractRef}${e.target_id}` : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-500">{e.detail ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
